import { useState, useEffect, useMemo } from 'react';
import { useIsStandalone } from './useIsStandalone';

export function detectBrowser() {
  if (typeof window === 'undefined') {
    return { name: 'browser', isEdge: false, isChrome: false, isFirefox: false, isSafari: false };
  }

  const ua = navigator.userAgent || '';
  const isEdge = /Edg\//.test(ua);
  const isFirefox = /Firefox\//.test(ua);
  const isChrome = /Chrome\//.test(ua) && !isEdge;
  const isSafari = /Safari\//.test(ua) && !isChrome && !isEdge;

  let name = 'your browser';
  if (isEdge) name = 'Microsoft Edge';
  else if (isChrome) name = 'Google Chrome';
  else if (isFirefox) name = 'Firefox';
  else if (isSafari) name = 'Safari';

  return { name, isEdge, isChrome, isFirefox, isSafari };
}

export function detectInstallPlatform() {
  if (typeof window === 'undefined') {
    return {
      isIOS: false,
      isAndroid: false,
      isInAppBrowser: false,
      isDesktop: true,
      browser: detectBrowser()
    };
  }

  const ua = navigator.userAgent || '';
  const isIOS =
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isAndroid = /android/i.test(ua);
  const isInAppBrowser = /FBAN|FBAV|Instagram|Line\/|Twitter|LinkedInApp|Snapchat/i.test(ua);

  return {
    isIOS,
    isAndroid,
    isInAppBrowser,
    isDesktop: !isIOS && !isAndroid,
    browser: detectBrowser()
  };
}

/**
 * PWA install state: standalone detection, native install prompt, and platform hints.
 */
export function usePwaInstall() {
  const isStandalone = useIsStandalone();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const platform = useMemo(() => detectInstallPlatform(), []);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const canNativeInstall = Boolean(deferredPrompt);
  const showInstallOption = !isStandalone;

  const promptInstall = async () => {
    if (!deferredPrompt) return { outcome: 'unavailable' };
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') setDeferredPrompt(null);
    return { outcome };
  };

  return {
    isStandalone,
    canNativeInstall,
    platform,
    promptInstall,
    showInstallOption
  };
}

/** @deprecated Use usePwaInstall — kept for imports that used alternate casing. */
export const usePWAInstall = usePwaInstall;
